import { createRouter, createWebHistory } from 'vue-router';
import { useClientAuthStore } from '../stores/auth';
import type { ClientCustomerPayload } from '../stores/auth';
import { useCheckoutStore } from '../stores/checkout';
import { useAppMetaStore } from '../stores/appMeta';

/**
 * Cliente criado via Google não tem telefone (a API do Google não devolve
 * isso) — esse gate tem prioridade sobre o de nome incompleto (OTP-only sem
 * nome), já que sem telefone nem o WhatsApp de notificação funciona.
 */
function profileGate(customer: ClientCustomerPayload | null): 'phone' | 'name' | null {
    if (!customer) return null;
    if (!customer.phone || customer.phone.trim() === '') return 'phone';
    if ((customer.name?.trim().length ?? 0) < 2) return 'name';

    return null;
}

function getBase(): string {
    // Subdomínio dinâmico: rotas do PWA cliente são sempre relativas a `/`
    // (ex.: armazem.borabeber.app/checkout/identify). O caminho legado
    // `/pwa/client/` foi removido com a migração para subdomínios.
    return '/';
}

const router = createRouter({
    history: createWebHistory(getBase()),
    routes: [
        { path: '/', name: 'catalog', component: () => import('../pages/Catalog.vue') },
        { path: '/demo', name: 'demo', component: () => import('../pages/Home.vue') },
        { path: '/login', name: 'login', component: () => import('../pages/Login.vue') },
        { path: '/register', name: 'register', component: () => import('../pages/Register.vue') },
        {
            path: '/completar-cadastro',
            name: 'complete-google-profile',
            component: () => import('../pages/CompleteGoogleProfile.vue'),
        },
        {
            path: '/esqueci-senha',
            name: 'forgot-password',
            component: () => import('../pages/ForgotPassword.vue'),
        },
        {
            path: '/reset-password',
            name: 'reset-password',
            component: () => import('../pages/ResetPassword.vue'),
        },
        {
            path: '/auth/google/complete',
            name: 'auth-google-complete',
            component: () => import('../pages/GoogleCallback.vue'),
        },
        {
            path: '/checkout/identify',
            name: 'checkout-identify',
            component: () => import('../pages/CheckoutIdentify.vue'),
        },
        {
            path: '/checkout/address',
            name: 'checkout-address',
            component: () => import('../pages/CheckoutAddress.vue'),
        },
        {
            path: '/checkout/payment',
            name: 'checkout-payment',
            component: () => import('../pages/CheckoutPayment.vue'),
        },
        {
            path: '/order/:id/confirmation',
            name: 'order-confirmation',
            component: () => import('../pages/OrderConfirmation.vue'),
        },
        {
            path: '/pedido/:orderNumber',
            name: 'order-tracking',
            component: () => import('../pages/OrderTracking.vue'),
        },
        { path: '/carrinho', name: 'carrinho', component: () => import('../pages/Carrinho.vue') },
        { path: '/perfil', name: 'perfil', component: () => import('../pages/Perfil.vue') },
        { path: '/perfil/editar', name: 'perfil-edit', component: () => import('../pages/EditProfile.vue') },
        { path: '/perfil/enderecos', name: 'perfil-addresses', component: () => import('../pages/ProfileAddresses.vue') },
        { path: '/meus-pedidos', name: 'meus-pedidos', component: () => import('../pages/MeusPedidos.vue') },
        { path: '/mensagens', name: 'mensagens', component: () => import('../pages/MessagesInbox.vue') },
        { path: '/mensagens/conversa', name: 'mensagens-conversa', component: () => import('../pages/MessagesThread.vue') },
        // Episódio ENCERRADO lido read-only por id (D-CHAT-18) — backend autoriza o dono.
        { path: '/mensagens/conversa/:episodeId', name: 'mensagens-episodio', component: () => import('../pages/MessagesThread.vue') },
    ],
});

router.beforeEach(async (to, _from, next) => {
    const auth = useClientAuthStore();

    if (to.path === '/register') {
        await auth.ensureCustomerLoaded();
        const intentParam = typeof to.query.intent === 'string' ? to.query.intent : null;
        const intentOk = intentParam === 'checkout' || intentParam === 'profile';
        const nameLen = auth.customer?.name?.trim().length ?? 0;

        if (auth.token) {
            if (intentOk && nameLen >= 2) {
                const dest = intentParam === 'checkout' ? '/checkout/address' : '/';

                return next({ path: dest, replace: true });
            }
            if (intentOk && nameLen < 2) {
                return next();
            }

            return next({ path: '/', replace: true });
        }

        return next();
    }

    if (to.path === '/completar-cadastro' || to.path === '/perfil/editar' || to.path === '/perfil/enderecos' || to.path === '/meus-pedidos' || to.path.startsWith('/mensagens')) {
        if (!auth.token) {
            return next({ path: '/login', query: { redirect: to.fullPath }, replace: true });
        }
        await auth.ensureCustomerLoaded();
    }

    // Chat Web (D-CHAT-11/12): sem o canal habilitado no tenant, a aba não existe.
    if (to.path.startsWith('/mensagens')) {
        const meta = useAppMetaStore();
        await meta.fetchMeta();
        if (!meta.webChatEnabled) {
            return next({ path: '/perfil', replace: true });
        }
    }

    const guardedCheckoutPaths = ['/checkout/identify', '/checkout/address', '/checkout/payment'];
    if (guardedCheckoutPaths.includes(to.path)) {
        const meta = useAppMetaStore();
        if (!meta.ready) {
            await meta.fetchMeta();
        }
        // Só o status (payload mínimo, com timeout) — não o catálogo inteiro.
        if (meta.tenantId && (await meta.refreshStoreStatus()) === false) {
            return next({ path: '/carrinho', query: { fechado: '1' }, replace: true });
        }
    }

    if (to.path === '/checkout/address') {
        if (!auth.token) {
            return next({ path: '/checkout/identify', replace: true });
        }
        await auth.ensureCustomerLoaded();
        const gate = profileGate(auth.customer);
        if (gate === 'phone') {
            return next({ path: '/completar-cadastro', query: { redirect: '/checkout/address' }, replace: true });
        }
        if (gate === 'name') {
            return next({
                path: '/register',
                query: { intent: 'checkout' },
                replace: true,
            });
        }

        return next();
    }

    if (to.path === '/checkout/identify') {
        await auth.ensureCustomerLoaded();
        const gate = profileGate(auth.customer);
        if (auth.token && gate === null) {
            return next({ path: '/checkout/address', replace: true });
        }
        if (auth.token && gate === 'phone') {
            return next({ path: '/completar-cadastro', query: { redirect: '/checkout/address' }, replace: true });
        }
        if (auth.token && gate === 'name') {
            return next({
                path: '/register',
                query: { intent: 'checkout' },
                replace: true,
            });
        }

        if ('step' in to.query) {
            return next({ path: '/checkout/identify', replace: true });
        }

        return next();
    }

    if (to.path === '/checkout/payment') {
        if (!auth.token) {
            return next({ path: '/checkout/identify', replace: true });
        }
        await auth.ensureCustomerLoaded();
        const gatePay = profileGate(auth.customer);
        if (gatePay === 'phone') {
            return next({ path: '/completar-cadastro', query: { redirect: '/checkout/address' }, replace: true });
        }
        if (gatePay === 'name') {
            return next({
                path: '/register',
                query: { intent: 'checkout' },
                replace: true,
            });
        }
        const co = useCheckoutStore();
        if (!co.addressId && !co.ephemeralDelivery) {
            return next({ path: '/checkout/address', replace: true });
        }

        return next();
    }

    if (to.name === 'order-confirmation') {
        if (!auth.token) {
            return next({ path: '/checkout/identify', replace: true });
        }
        await auth.ensureCustomerLoaded();

        return next();
    }

    if (to.name === 'order-tracking') {
        if (!auth.token) {
            return next({ path: '/login', query: { redirect: to.fullPath }, replace: true });
        }
        await auth.ensureCustomerLoaded();

        return next();
    }

    return next();
});

// Recarrega a página quando o hash do bundle muda (deploy novo durante a sessão).
// O SW com skipWaiting já invalida o cache, mas a aba aberta ainda tem refs antigas
// pros chunks dinâmicos — sem isso, clicar em qualquer link quebra com TypeError.
router.onError((error) => {
    const msg = error.message ?? '';
    if (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('error loading dynamically imported module')
    ) {
        window.location.reload();
    }
});

export default router;
